import { useEffect, useState } from 'react';
import {
  listSellerProfiles,
  createSellerProfile,
  updateSellerProfile,
  setDefaultSellerProfile,
  deleteSellerProfile,
} from '../../api/admin/sellerProfiles';

const EMPTY = {
  title: '',
  bankLabel: '',
  bankName: '',
  bik: '',
  correspondent: '',
  settlement: '',
  recipientName: '',
  recipientInn: '',
  recipientOgrnip: '',
  recipientKpp: '',
  address: '',
  signatoryName: '',
  isActive: true,
};

export default function AdminSellerProfiles() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const list = await listSellerProfiles();
      setItems(list);
    } catch (e) {
      alert(e?.response?.data?.error || 'Не удалось загрузить реквизиты');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function startCreate() {
    setEditingId('new');
    setForm({ ...EMPTY, title: `Вариант ${(items.length || 0) + 1}` });
  }

  function startEdit(item) {
    setEditingId(item._id);
    setForm({
      title: item.title || '',
      bankLabel: item.bankLabel || '',
      bankName: item.bankName || '',
      bik: item.bik || '',
      correspondent: item.correspondent || '',
      settlement: item.settlement || '',
      recipientName: item.recipientName || '',
      recipientInn: item.recipientInn || '',
      recipientOgrnip: item.recipientOgrnip || '',
      recipientKpp: item.recipientKpp || '',
      address: item.address || '',
      signatoryName: item.signatoryName || '',
      isActive: item.isActive !== false,
    });
  }

  function setField(key) {
    return (e) => {
      const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((p) => ({ ...p, [key]: value }));
    };
  }

  async function save() {
    if (!form.title.trim()) return alert('Укажите название варианта');
    setSaving(true);
    try {
      if (editingId === 'new') {
        await createSellerProfile(form);
      } else {
        await updateSellerProfile(editingId, form);
      }
      setEditingId(null);
      setForm(EMPTY);
      await load();
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  }

  async function makeDefault(id) {
    await setDefaultSellerProfile(id);
    await load();
  }

  async function remove(id) {
    if (!window.confirm('Удалить этот вариант реквизитов?')) return;
    try {
      await deleteSellerProfile(id);
      await load();
    } catch (e) {
      alert(e?.response?.data?.error || 'Не удалось удалить');
    }
  }

  if (loading) return <p>Загрузка…</p>;

  return (
    <div>
      <h2 className="admin-page-title">Реквизиты для документов</h2>
      <p className="hint">
        Варианты счетов/актов. По умолчанию используется вариант с меткой «default».
        При первом запуске создаётся «Вариант 1» из старых настроек (если были).
      </p>

      <div style={{ marginBottom: 12 }}>
        <button type="button" onClick={startCreate}>Добавить вариант</button>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Название</th>
            <th>Получатель</th>
            <th>ИНН</th>
            <th>Банк</th>
            <th>Статус</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it._id}>
              <td>
                {it.title}
                {it.isDefault && <span className="admin-badge admin-badge--active" style={{ marginLeft: 8 }}>default</span>}
              </td>
              <td>{it.recipientName || '—'}</td>
              <td>{it.recipientInn || '—'}</td>
              <td>{it.bankLabel || it.bankName || '—'}</td>
              <td>{it.isActive === false ? 'выкл' : 'активен'}</td>
              <td>
                <button type="button" className="edit-btn" onClick={() => startEdit(it)}>Изменить</button>
                {!it.isDefault && (
                  <button type="button" className="edit-btn" onClick={() => makeDefault(it._id)}>Сделать default</button>
                )}
                {!it.isDefault && (
                  <button type="button" className="muted" onClick={() => remove(it._id)}>Удалить</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editingId && (
        <section className="account__card" style={{ marginTop: 20, maxWidth: 720 }}>
          <h3>{editingId === 'new' ? 'Новый вариант' : 'Редактирование'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label>Название варианта *
              <input value={form.title} onChange={setField('title')} />
            </label>
            <label>Ярлык банка
              <input value={form.bankLabel} onChange={setField('bankLabel')} placeholder="Сбер" />
            </label>
            <label>Наименование банка
              <input value={form.bankName} onChange={setField('bankName')} />
            </label>
            <label>БИК
              <input value={form.bik} onChange={setField('bik')} />
            </label>
            <label>Корр. счёт
              <input value={form.correspondent} onChange={setField('correspondent')} />
            </label>
            <label>Расчётный счёт
              <input value={form.settlement} onChange={setField('settlement')} />
            </label>
            <label>Получатель (ИП/ООО) *
              <input value={form.recipientName} onChange={setField('recipientName')} />
            </label>
            <label>ИНН
              <input value={form.recipientInn} onChange={setField('recipientInn')} />
            </label>
            <label>ОГРНИП/ОГРН
              <input value={form.recipientOgrnip} onChange={setField('recipientOgrnip')} />
            </label>
            <label>КПП
              <input value={form.recipientKpp} onChange={setField('recipientKpp')} />
            </label>
            <label style={{ gridColumn: '1 / -1' }}>Адрес
              <input value={form.address} onChange={setField('address')} />
            </label>
            <label style={{ gridColumn: '1 / -1' }}>Подпись в актах (если пусто — получатель)
              <input value={form.signatoryName} onChange={setField('signatoryName')} placeholder="Манукян Г. В." />
            </label>
            <label>
              <input type="checkbox" checked={form.isActive} onChange={setField('isActive')} /> Активен
            </label>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button type="button" disabled={saving} onClick={save}>{saving ? 'Сохраняю…' : 'Сохранить'}</button>
            <button type="button" className="muted" onClick={() => { setEditingId(null); setForm(EMPTY); }}>Отмена</button>
          </div>
        </section>
      )}
    </div>
  );
}
